import {
  getItemProperties,
  ItemCategory,
  type ItemCategoryValue,
  type ItemTypeValue,
  STOCKPILE_CATEGORY_INFO,
  type StockpileItemSnapshot,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';

// ============================================================================
// Stockpile State Management
// ============================================================================
// Tracks the colony's virtual inventory - all "owned" resources
// Items in stockpile represent what the colony has claimed/gathered,
// regardless of physical location (ground, pawn inventory, etc.)
// ============================================================================

// Raw stockpile data: itemType -> quantity
const stockpileMap = signal<Map<ItemTypeValue, number>>(new Map());

// ============================================================================
// UI State
// ============================================================================

export const isStockpilePanelOpen = signal<boolean>(false);
export const activeStockpileCategory = signal<ItemCategoryValue>(ItemCategory.NONE); // NONE = All
export const stockpileSearchQuery = signal<string>('');

// ============================================================================
// Stockpile Item with Extended Info
// ============================================================================

export interface StockpileItemInfo {
  readonly itemType: ItemTypeValue;
  readonly quantity: number;
  readonly name: string;
  readonly category: ItemCategoryValue;
  readonly value: number;
  readonly totalValue: number;
}

// ============================================================================
// Actions - Server Sync
// ============================================================================

// Set full stockpile from server (initial sync)
export const setStockpile = ({ items }: { items: ReadonlyArray<StockpileItemSnapshot> }): void => {
  const newMap = new Map<ItemTypeValue, number>();
  for (const item of items) {
    if (item.quantity > 0) {
      newMap.set(item.itemType, item.quantity);
    }
  }
  stockpileMap.value = newMap;
};

// Update single item (delta update from server)
export const updateStockpileItem = ({
  itemType,
  quantity,
}: {
  itemType: ItemTypeValue;
  quantity: number;
}): void => {
  const newMap = new Map(stockpileMap.value);
  if (quantity <= 0) {
    newMap.delete(itemType);
  } else {
    newMap.set(itemType, quantity);
  }
  stockpileMap.value = newMap;
};

// Clear stockpile (on disconnect)
export const clearStockpile = (): void => {
  stockpileMap.value = new Map();
};

// ============================================================================
// Actions - UI
// ============================================================================

export const toggleStockpilePanel = (): void => {
  isStockpilePanelOpen.value = !isStockpilePanelOpen.value;
};

export const openStockpilePanel = (): void => {
  isStockpilePanelOpen.value = true;
};

export const closeStockpilePanel = (): void => {
  isStockpilePanelOpen.value = false;
};

export const setStockpileCategory = ({ category }: { category: ItemCategoryValue }): void => {
  activeStockpileCategory.value = category;
};

export const setStockpileSearch = ({ query }: { query: string }): void => {
  stockpileSearchQuery.value = query;
};

// ============================================================================
// Computed Values
// ============================================================================

// All stockpile items with extended info
export const stockpileItems = computed((): ReadonlyArray<StockpileItemInfo> => {
  const items: StockpileItemInfo[] = [];

  for (const [itemType, quantity] of stockpileMap.value) {
    const props = getItemProperties({ itemType });
    items.push({
      itemType,
      quantity,
      name: props.name,
      category: props.category,
      value: props.value,
      totalValue: props.value * quantity,
    });
  }

  // Sort by category, then by name
  items.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category - b.category;
    }
    return a.name.localeCompare(b.name);
  });

  return items;
});

// Filtered stockpile items (by category + search)
export const filteredStockpileItems = computed((): ReadonlyArray<StockpileItemInfo> => {
  const category = activeStockpileCategory.value;
  const search = stockpileSearchQuery.value.toLowerCase().trim();

  return stockpileItems.value.filter((item) => {
    // Category filter (NONE = all)
    if (category !== ItemCategory.NONE && item.category !== category) {
      return false;
    }

    // Search filter
    if (search && !item.name.toLowerCase().includes(search)) {
      return false;
    }

    return true;
  });
});

// Total value of all stockpile items
export const stockpileTotalValue = computed((): number => {
  let total = 0;
  for (const item of stockpileItems.value) {
    total += item.totalValue;
  }
  return total;
});

// Total item count
export const stockpileTotalItems = computed((): number => {
  let total = 0;
  for (const item of stockpileItems.value) {
    total += item.quantity;
  }
  return total;
});

// Number of unique item types
export const stockpileUniqueTypes = computed((): number => {
  return stockpileMap.value.size;
});

// Category info for UI
export const stockpileCategories = computed(() => {
  return STOCKPILE_CATEGORY_INFO;
});

// Item count per category (for badge display)
export const stockpileCategoryCounts = computed((): Map<ItemCategoryValue, number> => {
  const counts = new Map<ItemCategoryValue, number>();

  for (const item of stockpileItems.value) {
    const current = counts.get(item.category) ?? 0;
    counts.set(item.category, current + item.quantity);
  }

  // Also set total for "All" category
  counts.set(ItemCategory.NONE, stockpileTotalItems.value);

  return counts;
});

// ============================================================================
// Utility Functions
// ============================================================================

// Get quantity of a specific item
export const getStockpileQuantity = ({ itemType }: { itemType: ItemTypeValue }): number => {
  return stockpileMap.value.get(itemType) ?? 0;
};

// Check if stockpile has at least a certain quantity of an item
export const hasStockpileItem = ({
  itemType,
  minQuantity = 1,
}: {
  itemType: ItemTypeValue;
  minQuantity?: number;
}): boolean => {
  return getStockpileQuantity({ itemType }) >= minQuantity;
};

// ============================================================================
// Hotkeys
// ============================================================================

export const STOCKPILE_PANEL_HOTKEY = 'I'; // I for Inventory
